import { createApiRef, FetchApi } from '@backstage/core-plugin-api';
import type {
  AdManagementApi,
  AdUser,
  AdGroup,
  AdGroupMembers,
  AdOu,
  CreateUserResult,
  ResetPasswordResult,
  GpgKeyInfo,
  GpgKeySummary,
} from './types';

export const adManagementApiRef = createApiRef<AdManagementApi>({
  id: 'plugin.ad-management.api',
});

export class AdManagementClient implements AdManagementApi {
  private readonly fetchApi: FetchApi;

  constructor(options: { fetchApi: FetchApi }) {
    this.fetchApi = options.fetchApi;
  }

  private async fetch<T>(path: string, options?: RequestInit): Promise<T> {
    const baseUrl = '/api/ad-management';
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, options);

    if (!response.ok) {
      const body = await response.text();
      throw new Error(
        `Request failed with status ${response.status}: ${body}`,
      );
    }

    return response.json();
  }

  // ── Users ─────────────────────────────────────

  async listUsers(search?: string): Promise<AdUser[]> {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    const result = await this.fetch<{ items: AdUser[] }>(`/users${query}`);
    return result.items;
  }

  async getUser(username: string): Promise<AdUser> {
    return this.fetch<AdUser>(`/users/${encodeURIComponent(username)}`);
  }

  async createUser(params: {
    username: string;
    firstName: string;
    lastName: string;
    email?: string;
    password?: string;
    gpgPublicKey?: string;
  }): Promise<CreateUserResult> {
    return this.fetch<CreateUserResult>('/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
  }

  async disableUser(username: string): Promise<void> {
    await this.fetch(`/users/${encodeURIComponent(username)}/disable`, {
      method: 'POST',
    });
  }

  async enableUser(username: string): Promise<void> {
    await this.fetch(`/users/${encodeURIComponent(username)}/enable`, {
      method: 'POST',
    });
  }

  async deleteUser(username: string): Promise<void> {
    await this.fetch(`/users/${encodeURIComponent(username)}`, {
      method: 'DELETE',
    });
  }

  async resetPassword(
    username: string,
    password?: string,
  ): Promise<ResetPasswordResult> {
    return this.fetch(`/users/${encodeURIComponent(username)}/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(password ? { password } : {}),
    });
  }

  // ── Groups ────────────────────────────────────

  async listGroups(search?: string): Promise<AdGroup[]> {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    const result = await this.fetch<{ items: AdGroup[] }>(`/groups${query}`);
    return result.items;
  }

  async getGroupMembers(groupName: string): Promise<AdGroupMembers> {
    return this.fetch<AdGroupMembers>(
      `/groups/${encodeURIComponent(groupName)}/members`,
    );
  }

  async createGroup(params: {
    groupName: string;
    description?: string;
  }): Promise<void> {
    await this.fetch('/groups', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
  }

  async deleteGroup(groupName: string): Promise<void> {
    await this.fetch(`/groups/${encodeURIComponent(groupName)}`, {
      method: 'DELETE',
    });
  }

  async addToGroup(groupName: string, username: string): Promise<void> {
    await this.fetch(`/groups/${encodeURIComponent(groupName)}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
  }

  async removeFromGroup(
    groupName: string,
    username: string,
  ): Promise<void> {
    await this.fetch(
      `/groups/${encodeURIComponent(groupName)}/members/${encodeURIComponent(username)}`,
      { method: 'DELETE' },
    );
  }

  // ── GPG Keys ──────────────────────────────────

  async getGpgKey(username: string): Promise<GpgKeyInfo> {
    return this.fetch<GpgKeyInfo>(`/users/${encodeURIComponent(username)}/gpg-key`);
  }

  async storeGpgKey(
    username: string,
    gpgPublicKey: string,
    uploadedBy?: string,
  ): Promise<{ username: string; keyId: string }> {
    return this.fetch(`/users/${encodeURIComponent(username)}/gpg-key`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gpgPublicKey, ...(uploadedBy ? { uploadedBy } : {}) }),
    });
  }

  async deleteGpgKey(username: string): Promise<void> {
    await this.fetch(`/users/${encodeURIComponent(username)}/gpg-key`, {
      method: 'DELETE',
    });
  }

  async listGpgKeys(): Promise<GpgKeySummary[]> {
    const result = await this.fetch<{ items: GpgKeySummary[] }>('/gpg-keys');
    return result.items;
  }

  // ── OUs ───────────────────────────────────────

  async listOUs(): Promise<AdOu[]> {
    const result = await this.fetch<{ items: AdOu[] }>('/ous');
    return result.items;
  }
}
